import React from 'react';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import {
  BsBuilding,
  BsGlobe2,
  BsClock,
  BsTelephone,
  BsArrowRight,
  BsCheckCircleFill,
} from 'react-icons/bs';

import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { PhoneInput } from '@/components/PhoneInput';
import { countryDropdownOptions } from '../Onboarding.constants';
import { useTimezones } from '@/hooks/useTimezones';
import styles from '../Onboarding.module.scss';

const CHECKMARK_COLOR = '#12B76A';
const ICON_COLOR = '#667085';
const ICON_SIZE = 14;

export const CreateCompany = ({ form, setForm }) => {
  const timezoneDropdownOptions = useTimezones();

  const handleStartTimeChange = (time) => {
    setForm({ ...form, startTime: time ? time.format('HH:mm:ss') : null });
  };

  const handleEndTimeChange = (time) => {
    setForm({ ...form, endTime: time ? time.format('HH:mm:ss') : null });
  };

  return (
    <div className={styles.createCompanyForm}>
      {/* Company Name */}
      <div className={styles.formFieldGroup}>
        <label className={styles.formFieldLabel}>
          <BsBuilding size={ICON_SIZE} color={ICON_COLOR} />
          Company Name
        </label>
        <Input
          value={form.companyName}
          onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          placeholder="Enter company name"
          rightBlock={
            form.companyName ? (
              <BsCheckCircleFill size={16} color={CHECKMARK_COLOR} />
            ) : null
          }
        />
      </div>

      {/* Country + Timezone (side by side) */}
      <div className={styles.formRow}>
        <div className={styles.formFieldGroup}>
          <label className={styles.formFieldLabel}>
            <BsGlobe2 size={ICON_SIZE} color={ICON_COLOR} />
            Country
          </label>
          <Dropdown
            options={countryDropdownOptions}
            value={form.companyLocation}
            selectStyles={borderedSelectStyles}
            placeholder="Select country"
            onChange={(option) => setForm({ ...form, companyLocation: option })}
          />
        </div>

        <div className={styles.formFieldGroup}>
          <label className={styles.formFieldLabel}>
            <BsClock size={ICON_SIZE} color={ICON_COLOR} />
            Timezone
          </label>
          <div className={styles.fieldWithCheckmark}>
            <Dropdown
              options={timezoneDropdownOptions}
              value={form.timezone}
              selectStyles={borderedSelectStyles}
              placeholder="Select timezone"
              onChange={(option) => setForm({ ...form, timezone: option })}
            />
            {form.timezone && (
              <BsCheckCircleFill size={16} color={CHECKMARK_COLOR} />
            )}
          </div>
        </div>
      </div>

      {/* Phone Number */}
      <div className={styles.formFieldGroup}>
        <label className={styles.formFieldLabel}>
          <BsTelephone size={ICON_SIZE} color={ICON_COLOR} />
          Phone Number
        </label>
        <PhoneInput
          value={form.phoneNumber}
          onChange={(value) => setForm({ ...form, phoneNumber: value })}
          placeholder="Enter phone number"
        />
      </div>

      {/* Office Timing */}
      <div className={styles.formFieldGroup}>
        <label className={styles.formFieldLabel}>
          <BsClock size={ICON_SIZE} color={ICON_COLOR} />
          Office Timing
        </label>
        <div className={styles.officeTimingRow}>
          {/* Start time */}
          <div className={styles.timingPickerWrapper}>
            <BsClock size={ICON_SIZE} className={styles.timingPickerIcon} />
            <TimePicker
              value={form.startTime ? dayjs(form.startTime, 'HH:mm:ss') : null}
              onChange={handleStartTimeChange}
              format="hh:mm A"
              use12Hours
              allowClear={false}
              placeholder="Start time"
            />
          </div>

          <BsArrowRight size={16} className={styles.timingArrow} />

          {/* End time */}
          <div className={styles.timingPickerWrapper}>
            <BsClock size={ICON_SIZE} className={styles.timingPickerIcon} />
            <TimePicker
              value={form.endTime ? dayjs(form.endTime, 'HH:mm:ss') : null}
              onChange={handleEndTimeChange}
              format="hh:mm A"
              use12Hours
              allowClear={false}
              placeholder="End time"
            />
          </div>

          {form.endTime && (
            <BsCheckCircleFill
              size={16}
              color={CHECKMARK_COLOR}
              className={styles.timingCheckmark}
            />
          )}
        </div>
      </div>
    </div>
  );
};
